import { SearchResult } from '../types/search-result';

export type SearchResultGroup = {
  type: SearchResult['type'];
  heading: string;
  results: SearchResult[];
};

const SEARCH_RESULT_GROUPS: Pick<SearchResultGroup, 'type' | 'heading'>[] = [
  { type: 'list', heading: 'Lists' },
  { type: 'item', heading: 'Items' },
  { type: 'index', heading: 'Indexes' },
  { type: 'identity', heading: 'Identities' },
  { type: 'token', heading: 'Tokens' },
  { type: 'user', heading: 'Users' },
  { type: 'docs', heading: 'Documentation' },
];

/**
 * Get the plural label for a search result type, e.g. "Lists"
 */
export function searchResultTypeLabel(type: string): string {
  return (
    SEARCH_RESULT_GROUPS.find(group => group.type === type)?.heading ?? type
  );
}

// Matches how the server splits a query into words and filters
const SEARCH_QUERY_TOKEN_PATTERN = /[^\s:]+:"[^"]*"?|\S+/g;

/**
 * Remove a filter (as it was written in the query) from a search query
 */
export function removeSearchQueryFilter(query: string, raw: string): string {
  const tokens: string[] = query.match(SEARCH_QUERY_TOKEN_PATTERN) ?? [];
  const index = tokens.indexOf(raw);

  return (index === -1 ? tokens : tokens.filter((_, i) => i !== index)).join(
    ' '
  );
}

/**
 * Add a type filter to a search query
 */
export function addSearchQueryTypeFilter(
  query: string,
  type: SearchResult['type']
): string {
  return `${query.trim()} type:${type}`.trim();
}

/**
 * Group search results by type, with the group containing the most relevant
 * result first, and documentation always at the bottom
 */
export function groupSearchResults(
  results: SearchResult[]
): SearchResultGroup[] {
  const best = (group: SearchResultGroup) =>
    Math.max(...group.results.map(result => result.relevance));

  return SEARCH_RESULT_GROUPS.map(group => ({
    ...group,
    results: results.filter(result => result.type === group.type),
  }))
    .filter(group => group.results.length > 0)
    .sort((a, b) => {
      if (a.type === 'docs' || b.type === 'docs') {
        return a.type === 'docs' ? 1 : -1;
      }

      return best(b) - best(a);
    });
}

/**
 * Get the dashboard url for a search result
 */
export function searchResultUrl(result: SearchResult): string {
  switch (result.type) {
    case 'list':
      return `/lists/${result.data.id}`;
    case 'item':
      return `/lists/${result.data.listId}/items/${result.data.id}`;
    case 'index':
      return `/lists/${result.data.listId}/indexes/${result.data.id}`;
    case 'identity':
      return `/identities/${result.data.id}`;
    case 'token':
      return `/tokens/${result.data.id}`;
    case 'user':
      return `/users/${result.data.id}`;
    case 'docs':
      return result.data.url;
  }
}
